import {
    add,
    differenceInMinutes,
    format,
    isAfter,
    isBefore,
    isSameDay,
    isValid,
    max,
    min,
    parse,
    parseISO,
    startOfDay,
    sub,
} from 'date-fns';
import { da, enGB, enUS } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

export const COPENHAGEN_TZ = 'Europe/Copenhagen';

export function toDateFnsPattern(pattern: string): string {
    let result = '';
    let index = 0;

    while (index < pattern.length) {
        if (pattern[index] === '[') {
            const end = pattern.indexOf(']', index);
            if (end === -1) {
                result += "'['";
                index += 1;
                continue;
            }
            result += `'${pattern.slice(index + 1, end)}'`;
            index = end + 1;
            continue;
        }

        const remaining = pattern.slice(index);
        const tokens: [string, string][] = [
            ['YYYY', 'yyyy'],
            ['YY', 'yy'],
            ['DD', 'dd'],
            ['Do', 'do'],
            ['dddd', 'EEEE'],
            ['ddd', 'EEE'],
            ['MMMM', 'MMMM'],
            ['MMM', 'MMM'],
            ['MM', 'MM'],
            ['HH', 'HH'],
            ['hh', 'hh'],
            ['mm', 'mm'],
            ['ss', 'ss'],
            ['A', 'aaa'],
            ['a', 'aaa'],
        ];

        let matched = false;
        for (const [from, to] of tokens) {
            if (remaining.startsWith(from)) {
                result += to;
                index += from.length;
                matched = true;
                break;
            }
        }

        if (matched) {
            continue;
        }

        const char = pattern[index];
        if (char === 'D') {
            result += 'd';
        } else if (char === 'M') {
            result += 'M';
        } else if (char === 'Y') {
            result += 'y';
        } else if (char === 'H') {
            result += 'H';
        } else if (char === 'h') {
            result += 'h';
        } else if (char === 'm') {
            result += 'm';
        } else if (char === 's') {
            result += 's';
        } else if (char === 'd') {
            result += 'E';
        } else if (/[A-Za-z]/.test(char)) {
            result += `'${char}'`;
        } else {
            result += char;
        }
        index += 1;
    }

    return result;
}

export function toDate(value: Date | string | number): Date {
    if (value instanceof Date) {
        return value;
    }
    return new Date(value);
}

function resolveInstant(
    value: Date | string | number | null | undefined
): Date | null {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    if (value instanceof Date) {
        return isValid(value) ? value : null;
    }

    if (typeof value === 'string') {
        const isoParsed = parseISO(value);
        if (isValid(isoParsed)) {
            return isoParsed;
        }

        const fallback = toDate(value);
        return isValid(fallback) ? fallback : null;
    }

    const date = toDate(value);
    return isValid(date) ? date : null;
}

function resolveFormattedDate(
    value: Date | string | number | null | undefined,
    pattern: string
): Date | null {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    if (value instanceof Date) {
        return isValid(value) ? value : null;
    }

    if (typeof value === 'string') {
        const parsed = parseWithPattern(value, pattern);
        if (isValid(parsed)) {
            return parsed;
        }

        return resolveInstant(value);
    }

    return resolveInstant(value);
}

function safeFormat(
    date: Date,
    pattern: string,
    options?: { locale?: Locale }
): string {
    if (!isValid(date)) {
        return '';
    }

    return format(date, toDateFnsPattern(pattern), options);
}

function safeFormatInTimeZone(
    date: Date,
    timeZone: string,
    pattern: string
): string {
    if (!isValid(date)) {
        return '';
    }

    return formatInTimeZone(date, timeZone, toDateFnsPattern(pattern));
}

export function formatWithPattern(
    value: Date | string | number | null | undefined,
    pattern: string,
    options?: { locale?: Locale; inputPattern?: string }
): string {
    if (value === undefined || value === null || value === '') {
        return '';
    }

    const parsePattern = options?.inputPattern ?? pattern;
    const date =
        options?.inputPattern !== undefined
            ? typeof value === 'string'
                ? parseWithPattern(value, parsePattern)
                : value instanceof Date
                  ? value
                  : resolveInstant(value)
            : resolveFormattedDate(value, pattern);

    if (!date || !isValid(date)) {
        return typeof value === 'string' ? value : '';
    }

    return safeFormat(date, pattern, options);
}

export function parseWithPattern(
    value: string,
    pattern: string,
    referenceDate: Date = new Date()
): Date {
    return parse(value, toDateFnsPattern(pattern), referenceDate);
}

export function formatLll(
    value: Date | string | number | null | undefined
): string {
    const date = resolveInstant(value);
    if (!date) {
        return '';
    }

    return format(date, 'MMM d, yyyy h:mm a', { locale: enUS });
}

export function formatInCopenhagen(
    value: Date | string | number | null | undefined,
    pattern: string
): string {
    const date = resolveInstant(value);
    if (!date) {
        return typeof value === 'string' ? value : '';
    }

    return safeFormatInTimeZone(date, COPENHAGEN_TZ, pattern);
}

export function utcFormat(
    value: Date | string | number | null | undefined,
    pattern: string
): string {
    const date = resolveInstant(value);
    if (!date) {
        return typeof value === 'string' ? value : '';
    }

    return safeFormatInTimeZone(date, 'UTC', pattern);
}

export function reinterpretAsCET(date: Date): string {
    if (!isValid(date)) {
        return '';
    }

    const wallClock = format(date, "yyyy-MM-dd'T'HH:mm:ss");
    return fromZonedTime(wallClock, COPENHAGEN_TZ).toISOString();
}

export function getCETTimestamp(dateString: string | Date): string {
    if (dateString === undefined || dateString === null || dateString === '') {
        return '';
    }

    const tzString = COPENHAGEN_TZ;
    const source =
        typeof dateString === 'string' ? new Date(dateString) : dateString;

    if (!isValid(source)) {
        return '';
    }

    const dateObject = toDate(
        source.toLocaleString('en-US', { timeZone: tzString })
    );

    if (!isValid(dateObject)) {
        return '';
    }

    return format(dateObject, "yyyy-MM-dd'T'HH:mm:ss");
}

export function parseZoneFormat(value: string, pattern: string): string {
    if (!value) {
        return '';
    }

    const date = parseISO(value);
    if (!isValid(date)) {
        return value;
    }

    if (value.endsWith('Z') || /[+-]00:00$/.test(value)) {
        return safeFormatInTimeZone(date, 'UTC', pattern);
    }

    const offsetMatch = value.match(/([+-])(\d{2}):?(\d{2})$/);
    if (offsetMatch) {
        const sign = offsetMatch[1] === '+' ? 1 : -1;
        const hours = parseInt(offsetMatch[2], 10);
        const minutes = parseInt(offsetMatch[3], 10);
        const shifted = add(date, {
            minutes: sign * (hours * 60 + minutes),
        });
        return safeFormatInTimeZone(shifted, 'UTC', pattern);
    }

    return safeFormat(date, pattern);
}

export {
    add,
    da,
    differenceInMinutes,
    enGB,
    enUS,
    format,
    formatInTimeZone,
    fromZonedTime,
    isAfter,
    isBefore,
    isSameDay,
    isValid,
    max,
    min,
    parse,
    parseISO,
    startOfDay,
    sub,
    toZonedTime,
};
